import { Contactpersoon, Functie, usePatchContactpersoon, usePostContactpersoon } from "@/entities/contactpersoon";
import { StationsAutocomplete } from "@/features/infra";
import {
    Button,
    Dialog,
    DialogBody,
    DialogContainer,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    Form,
    FormFieldInput,
    SelectFormField,
} from "@/shared/components";
import { ErrorDisplay } from "@/shared/components/error";
import { omitBy } from "@/shared/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import z, { ZodErrorMap } from "zod";
import { functieOptions, isLocatieFunctie, isTreinFunctie, locatiefuncties, treinfuncties } from "./utils";

const nl06telefoonnummer = z
    .string()
    .transform((s) => s.trim()) // leading/trailing whitespace
    .transform((s) => s.replace(/\s+/g, "")) // alle spaties weg
    .transform((s) => s.replace(/[-()]/g, "")) // streepjes, haakjes, etc.
    .refine((s) => /^06\d{8}$/.test(s), {
        message: "Voer een geldig 06-nummer in (bijv. 0612345678)",
    });

const nieuwContactpersoonBase = z.object({
    naam: z.string({ error: "Geef een naam op" }).trim().optional(),
    telefoonnummer: nl06telefoonnummer,
});

const nieuwContactpersoonOpTrein = z.object({
    functie: z.enum(treinfuncties, { error: "Selecteer functie" }),
    treinnummer: z.coerce
        .number<number>({
            error: (issue) =>
                issue.input === undefined ? "Vul het treinnummer in" : issue.code === "invalid_type" ? "Voer een getal in" : undefined,
        })
        .min(0),
});

const nieuwContactpersoonOpLocatie = z.object({
    functie: z.enum(locatiefuncties, { error: "Selecteer functie" }),
    stationcode: z.string(),
});
const errorMap: ZodErrorMap = (issue) => {
    if (issue.code === "invalid_union") {
        return { message: "Selecteer functie" };
    }
    return { message: "Unknown error" };
};

const nieuwContactpersoonSchema = nieuwContactpersoonBase.and(
    z.discriminatedUnion("functie", [nieuwContactpersoonOpTrein, nieuwContactpersoonOpLocatie], { error: errorMap })
);

type ContactpersoonPayload = {
    naam?: string | undefined;
    telefoonnummer: string;
    functie: Functie;
    stationcode?: string;
    treinnummer?: number;
};

type PreSelectedType = "OpLocatie" | "OpTrein";

type NieuwContactpersoonData = z.infer<typeof nieuwContactpersoonSchema>;

const getDefaultValues = (defaultValues?: Partial<ContactpersoonPayload>, preSelectedType?: PreSelectedType) => {
    if (!defaultValues) return {};

    const base = {
        naam: defaultValues.naam,
        telefoonnummer: defaultValues.telefoonnummer ?? "",
        functie: defaultValues.functie ?? "",
    };

    if (preSelectedType === "OpTrein") {
        return {
            ...base,
            treinnummer: defaultValues.treinnummer ?? 0,
        };
    } else if (preSelectedType === "OpLocatie") {
        return {
            ...base,
            stationcode: defaultValues.stationcode ?? "",
        };
    }

    return base;
};

type NieuwEditContactpersoonDialogProps = {
    open: boolean;
    onOpenChange: React.Dispatch<React.SetStateAction<boolean>>;
    onSubmit: (payload: ContactpersoonPayload) => Promise<void>;
    title?: string;
    defaultValues?: Partial<Contactpersoon>;
    functieReadOnly?: boolean;
};

export function NieuwEditContactpersoonDialog({
    onSubmit,
    defaultValues,
    functieReadOnly,
    open,
    onOpenChange,
    title,
}: NieuwEditContactpersoonDialogProps) {
    const [submitError, setSubmitError] = useState<Error | null>(null);
    const preSelectedType: PreSelectedType | undefined = defaultValues?.stationcode
        ? "OpLocatie"
        : defaultValues?.treinnummer
          ? "OpTrein"
          : undefined;
    const form = useForm<NieuwContactpersoonData>({
        resolver: zodResolver(nieuwContactpersoonSchema),
        defaultValues: getDefaultValues(defaultValues, preSelectedType),
    });

    const handleSubmit: SubmitHandler<NieuwContactpersoonData> = async (data) => {
        try {
            setSubmitError(null);
            await onSubmit(data);
            onOpenChange(false);
            form.reset();
        } catch (error) {
            setSubmitError(error as Error);
        }
    };

    const onCancel = () => {
        onOpenChange(false);
        form.reset();
    };

    const functie = form.watch("functie");

    const functieDropdownOptions = functieOptions.filter((option) => {
        if (preSelectedType === "OpLocatie") {
            return isLocatieFunctie(option.value);
        } else if (preSelectedType === "OpTrein") {
            return isTreinFunctie(option.value);
        } else {
            return true;
        }
    });

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContainer className="min-w-112.5">
                <DialogHeader>
                    <DialogTitle>{title || "Nieuw contactpersoon"}</DialogTitle>
                </DialogHeader>
                <Form {...form}>
                    <form onSubmit={(e) => void form.handleSubmit(handleSubmit)(e)}>
                        <DialogBody className="grid gap-2">
                            <div className="grid grid-cols-2 gap-2">
                                <DialogDescription className="sr-only">Maak een nieuw contactpersoon aan</DialogDescription>
                                <div>
                                    <SelectFormField
                                        form={form}
                                        name="functie"
                                        label="Functie"
                                        options={functieDropdownOptions}
                                        readOnly={!!functieReadOnly}
                                    />
                                </div>
                                <div>
                                    <FormFieldInput form={form} name="naam" label="Naam" autoFocus={!!functieReadOnly} />
                                </div>
                            </div>
                            <FormFieldInput
                                form={form}
                                name="telefoonnummer"
                                label="Telefoonnummer"
                                readOnly={!!defaultValues?.telefoonnummer}
                            />
                            {(isTreinFunctie(functie) || preSelectedType === "OpTrein") && (
                                <FormFieldInput
                                    form={form}
                                    name="treinnummer"
                                    label="Treinnummer"
                                    readOnly={!!defaultValues?.treinnummer}
                                />
                            )}
                            {isLocatieFunctie(functie) && (
                                <StationsAutocomplete
                                    form={form}
                                    name="stationcode"
                                    placeholder="Zoek station..."
                                    nlStationsOnly
                                    data-testid="contactpersoon-add-station"
                                />
                            )}
                        </DialogBody>
                        <DialogFooter className="flex flex-col gap-2">
                            {submitError && <ErrorDisplay error={submitError} title="Fout bij het aanmaken van contactpersoon" />}
                            <div className="flex flex-row justify-end gap-2">
                                <Button type="button" variant="tertiary" onClick={onCancel}>
                                    Annuleren
                                </Button>
                                <Button type="submit" variant="primary" disabled={form.formState.isSubmitting}>
                                    Opslaan
                                </Button>
                            </div>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContainer>
        </Dialog>
    );
}

type NieuwContactpersoonProps = {
    dossierId: string;
    onCreate?: (contact: Contactpersoon) => void;
    functie?: Functie;
    stationcode?: string;
    treinnummer?: number;
    open: boolean;
    onOpenChange: React.Dispatch<React.SetStateAction<boolean>>;
};

export function NieuwContactpersoon({
    dossierId,
    onCreate,
    functie,
    stationcode,
    treinnummer,
    open,
    onOpenChange,
}: NieuwContactpersoonProps) {
    const postContactpersoon = usePostContactpersoon();

    const createContactpersoon = async (data: ContactpersoonPayload) => {
        const result = await postContactpersoon.mutateAsync({ ...data, dossierId });
        onCreate?.(Contactpersoon.fromDto(result));
    };

    return (
        <NieuwEditContactpersoonDialog
            key={`${open}`} // key makes sure react hook form doesn't hold on to old default values when re-opening the dialog
            onSubmit={createContactpersoon}
            functieReadOnly={functie !== undefined}
            defaultValues={omitBy({ treinnummer, functie, stationcode }, (value) => value === undefined)}
            open={open}
            onOpenChange={onOpenChange}
            title={functie && `Nieuwe ${functie} toevoegen`}
        />
    );
}

type EditContactpersoonProps = {
    dossierId: string;
    contactpersoon: Contactpersoon;
    open: boolean;
    onOpenChange: React.Dispatch<React.SetStateAction<boolean>>;
};

export function EditContactpersoon({ dossierId, contactpersoon, open, onOpenChange }: EditContactpersoonProps) {
    const patchContactpersoon = usePatchContactpersoon();

    const editContactpersoon = async (data: ContactpersoonPayload) => {
        await patchContactpersoon.mutateAsync({ ...data, dossierId, contactpersoonId: contactpersoon.id });
    };

    return (
        <NieuwEditContactpersoonDialog
            key={`${contactpersoon.id}-${open}`} // key makes sure react hook form doesn't hold on to old values when re-opening the dialog
            onSubmit={editContactpersoon}
            defaultValues={contactpersoon}
            open={open}
            onOpenChange={onOpenChange}
            title="Contactpersoon bewerken"
        />
    );
}
